export type PrivacyPreferences = {
  diagnosticsEnabled: boolean;
};

const STORAGE_KEY = "monyawn-privacy-preferences-v1";
const CHANGE_EVENT = "monyawn:privacy-preferences-changed";

const defaultPreferences: PrivacyPreferences = {
  diagnosticsEnabled: false,
};

function isPreferences(input: unknown): input is PrivacyPreferences {
  if (!input || typeof input !== "object") {
    return false;
  }

  const candidate = input as Record<string, unknown>;
  return typeof candidate.diagnosticsEnabled === "boolean";
}

export function getDefaultPrivacyPreferences(): PrivacyPreferences {
  return defaultPreferences;
}

export function getPrivacyPreferences(): PrivacyPreferences {
  if (typeof window === "undefined") {
    return defaultPreferences;
  }

  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return defaultPreferences;
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (isPreferences(parsed)) {
      return parsed;
    }
  } catch {
    return defaultPreferences;
  }

  return defaultPreferences;
}

export function hasStoredPrivacyPreferences() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(STORAGE_KEY) !== null;
}

export function savePrivacyPreferences(preferences: PrivacyPreferences) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: preferences }));
}

export function subscribeToPrivacyPreferences(listener: (preferences: PrivacyPreferences) => void) {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const handleChange = (event: Event) => {
    const customEvent = event as CustomEvent<PrivacyPreferences>;
    listener(customEvent.detail ?? getPrivacyPreferences());
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) {
      listener(getPrivacyPreferences());
    }
  };

  window.addEventListener(CHANGE_EVENT, handleChange as EventListener);
  window.addEventListener("storage", handleStorage);

  return () => {
    window.removeEventListener(CHANGE_EVENT, handleChange as EventListener);
    window.removeEventListener("storage", handleStorage);
  };
}

export function clearPrivacyPreferences() {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: defaultPreferences }));
}

export function areDiagnosticsEnabled() {
  return getPrivacyPreferences().diagnosticsEnabled;
}
